/**
 * F2.c - ProgressionHero
 *
 * Calm, editorial rank + progress card. Ember/navy palette. No coins,
 * no confetti, no arcade sound cues. Respects `prefers-reduced-motion`.
 *
 * Data source: GET /api/me/progression
 * Renders:
 *   - Rank display ("Naya Dost II") with level number badge
 *   - Points total (compact)
 *   - Segmented progress bar to next level with tooltip trigger listing
 *     the top-earning actions ("How to earn points")
 *   - Next-rank preview line (only when next level lives in a new rank)
 *   - Recent activity strip: 3 most recent ledger rows as small chips
 *
 * Copy rule: middle dot (·) as separator. Never em/en dashes.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { Info, Sparkles } from 'lucide-react';
import api from '@/utils/api';

// Human-readable labels for ledger `kind` values. Never renders raw enum
// strings to the user. Any unknown kind falls back to a title-cased slug.
const KIND_LABELS = {
  event_checkin: 'Verified check-in',
  event_hosted: 'Hosted an event',
  referral_verified_attendance: 'Referral attended',
  full_result_imported: 'Full result imported',
  survey_completed: 'Survey completed',
  assessment_added: 'Assessment added',
  rsvp_confirmed: 'RSVP confirmed',
  rsvp_submitted: 'RSVP submitted',
  profile_section_complete: 'Profile section',
  profile_photo_upload: 'Photo uploaded',
  admin_manual_adjust: 'Admin adjustment',
};

const labelFor = (kind = '') =>
  KIND_LABELS[kind] ||
  kind.replace(/^revoke:/, 'Reversal · ')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

const formatPoints = (n) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

const ActivityChip = ({ row, index }) => {
  const isPositive = (row.points || 0) >= 0;
  return (
    <div
      data-testid={`progression-hero-activity-${index}`}
      className="inline-flex items-center gap-1.5 rounded-full border border-primary-100 bg-white/70 px-3 py-1 text-xs backdrop-blur-sm"
    >
      <span className="text-primary-800 font-medium">{labelFor(row.kind)}</span>
      <span
        className={
          isPositive ? 'text-ember-700 font-semibold' : 'text-neutral-500 font-semibold'
        }
      >
        {isPositive ? '+' : ''}
        {row.points}
      </span>
    </div>
  );
};

const EarnTooltip = ({ actions }) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="How to earn Dosti Points"
          data-testid="progression-earn-tooltip-trigger"
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-primary-500 hover:text-primary-800 transition-colors focus:outline-none focus:ring-2 focus:ring-secondary-400 rounded-sm px-1"
        >
          <Info className="h-3 w-3" /> How to earn
        </button>
      </TooltipTrigger>
      <TooltipContent
        data-testid="progression-earn-tooltip"
        side="top"
        className="max-w-xs bg-primary-900 text-primary-50 border-primary-800 text-xs leading-relaxed"
      >
        <div className="font-semibold mb-1 text-secondary-300">Top ways to earn</div>
        <div>
          {actions.map((a, i) => (
            <React.Fragment key={a.kind}>
              {i > 0 && <span className="text-primary-400"> · </span>}
              <span className="whitespace-nowrap">
                {a.label} <span className="text-secondary-300 font-semibold">{a.points}</span>
              </span>
            </React.Fragment>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const ProgressionHeroSkeleton = () => (
  <div
    data-testid="progression-hero-skeleton"
    className="rounded-2xl border border-primary-100 bg-white h-40 animate-pulse"
    aria-hidden="true"
  />
);

/**
 * Renders the calm progression card for the currently authenticated user.
 * Silently renders nothing on error - never blocks the surrounding page.
 */
export const ProgressionHero = ({ className = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data: resp } = await api.get('/me/progression');
        if (!cancelled) setData(resp);
      } catch (e) {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // F2.e - re-fetch whenever a progression delta lands so rank, level,
    // and points_total stay in sync with the toast/modal the user just saw.
    const onDelta = () => { load(); };
    window.addEventListener('dosti:progression_delta', onDelta);
    return () => {
      cancelled = true;
      window.removeEventListener('dosti:progression_delta', onDelta);
    };
  }, []);

  if (loading) return <ProgressionHeroSkeleton />;
  if (failed || !data) return null;

  const p = data.progression || {};
  const recent = (data.recent_ledger || []).slice(0, 3);
  const actions = data.top_earning_actions || [];

  const rankDisplay = p.rank_display || 'Naya Dost I';
  const subtitle = p.subtitle || '';
  const level = p.level || 1;
  const pointsTotal = p.points_total || 0;
  const pct = p.level_progress_pct ?? 0;
  const isMax = !!p.is_max_level;
  const into = p.points_into_level ?? 0;
  const need = p.points_needed_this_level ?? 0;
  const nextLevel = p.next_level;
  const nextRankDisplay = p.next_rank_display;

  return (
    <Card
      data-testid="progression-hero-root"
      className={`overflow-hidden border border-primary-100 bg-gradient-to-br from-primary-50/60 via-white to-secondary-50/40 shadow-soft motion-reduce:transform-none ${className}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          {/* Rank + level identity */}
          <div className="flex items-center gap-4 min-w-0">
            <div
              data-testid="progression-hero-level-number"
              className="relative flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-ember-700 to-ember-900 text-white flex items-center justify-center shadow-inner"
              aria-label={`Level ${level}`}
            >
              <div className="text-[10px] uppercase tracking-widest text-secondary-300 absolute top-1.5">
                Lv
              </div>
              <div className="font-display text-2xl font-bold leading-none mt-2">
                {level}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest text-primary-500 mb-0.5">
                Current rank
              </div>
              <h2
                data-testid="progression-hero-rank-display"
                className="font-display text-xl md:text-2xl font-bold text-primary-900 leading-tight truncate"
              >
                {rankDisplay}
              </h2>
              {subtitle && (
                <p
                  data-testid="progression-hero-rank-subtitle"
                  className="text-sm text-primary-600 italic mt-0.5 truncate"
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Points total */}
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-widest text-primary-500 mb-0.5">
              Dosti Points
            </div>
            <div className="flex items-baseline justify-end gap-1">
              <Sparkles className="h-4 w-4 text-secondary-600" />
              <span
                data-testid="progression-hero-points-total"
                className="font-display text-2xl md:text-3xl font-bold text-primary-900 tabular-nums"
              >
                {formatPoints(pointsTotal)}
              </span>
            </div>
          </div>
        </div>

        {/* Progress bar + caption + tooltip */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-3">
              <span
                data-testid="progression-hero-progress-caption"
                className="text-xs text-primary-700"
              >
                {isMax ? (
                  <>Max level reached · {formatPoints(pointsTotal)} lifetime</>
                ) : (
                  <>
                    <span className="font-semibold text-primary-900">{into}</span>
                    <span className="text-primary-500"> of </span>
                    <span className="font-semibold text-primary-900">{need}</span>
                    <span className="text-primary-500"> points to Level {nextLevel}</span>
                  </>
                )}
              </span>
              {actions.length > 0 && <EarnTooltip actions={actions} />}
            </div>
            <span className="text-xs text-primary-500 tabular-nums">{pct}%</span>
          </div>
          <Progress
            data-testid="progression-hero-progress-bar"
            value={isMax ? 100 : pct}
            aria-label={
              isMax
                ? 'Max level progression complete'
                : `${pct}% progress to level ${nextLevel}`
            }
            className="h-2 bg-primary-100 [&>div]:bg-gradient-to-r [&>div]:from-ember-500 [&>div]:to-ember-700"
          />

          {/* Next-rank preview: only shown when a rank change is next up */}
          {nextRankDisplay && (
            <div
              data-testid="progression-hero-next-rank"
              className="mt-2 text-xs text-primary-600"
            >
              Coming next · <span className="font-semibold text-primary-800">{nextRankDisplay}</span>
            </div>
          )}
        </div>

        {/* Recent activity strip */}
        {recent.length > 0 && (
          <div className="mt-5 pt-4 border-t border-primary-100">
            <div className="text-[11px] uppercase tracking-widest text-primary-500 mb-2">
              Recent activity
            </div>
            <div
              data-testid="progression-hero-activity-strip"
              className="flex flex-wrap gap-2"
            >
              {recent.map((row, i) => (
                <ActivityChip key={row.id || i} row={row} index={i} />
              ))}
            </div>
          </div>
        )}

        {recent.length === 0 && (
          <div
            data-testid="progression-hero-empty-activity"
            className="mt-5 pt-4 border-t border-primary-100 text-xs text-primary-500 italic"
          >
            No activity yet. RSVP to an event or complete a profile section to start earning.
          </div>
        )}
        <div className="mt-4 text-right">
          <a
            href="/how-progression-works"
            data-testid="progression-hero-learn-more"
            className="text-xs text-secondary-700 hover:underline"
          >
            Learn how points work
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProgressionHero;
